// helpers
import { ApiHelper } from 'shared/helpers/api';
import { getUrl, ROUTES } from 'shared/api/urls';
import { getEddHeader, getONRHeader } from 'shared/api/cart/helpers';

// constants
import { API_ERROR_MESSAGE } from 'shared/constants/messages';

// defs
import { PincodeServicable, AddressDataApi } from 'shared/types/address';

const NO_ADDRESS_FOUND_CODE = 1010;

export const getAddresses = () => {
  const url = getUrl(ROUTES.GET_ADDRESSES);

  return ApiHelper(url, {
    headers: {
      'x-web': '1',
    },
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ statusCode: response.status, message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.status !== 'success' && res.code !== NO_ADDRESS_FOUND_CODE) {
      return Promise.reject(res);
    }

    if (res.code === NO_ADDRESS_FOUND_CODE) {
      return [];
    }

    return res.response.addresses as AddressDataApi[];
  });
};

export interface AddAddressArgs {
  city: string;
  isDefault: boolean;
  mobileNumber: string;
  state: string;
  fullName: string;
  addressLineTwo: string;
  country: string;
  pincode: string;
  countryId: string;
  regionCode: string;
  regionId: string;
  addressLineOne: string;
  email: string;
}

export const addAddress = ({
  city,
  isDefault,
  mobileNumber,
  state,
  fullName,
  addressLineOne,
  addressLineTwo,
  country,
  pincode,
  countryId,
  regionCode,
  regionId,
  email,
}: AddAddressArgs) => {
  const body = {
    address: {
      city,
      isDefault,
      mobileNumber,
      state,
      fullName,
      addressLineOne,
      addressLineTwo,
      country,
      pincode,
      countryId,
      regionCode,
      regionId,
      email,
    },
  };

  const url = getUrl(ROUTES.ADD_ADDRESS);

  return ApiHelper(url, {
    method: 'POST',
    body,
    headers: {
      'x-web': '1',
    },
    isBodyJSON: true,
  })
    .then(async (response) => {
      if (response.ok === false) {
        return Promise.reject({ message: API_ERROR_MESSAGE });
      }

      const res = await response.json();

      if (res.status !== 'success') {
        return Promise.reject(res);
      }

      return { address: res.response as AddressDataApi, message: res.message };
    })
    .catch((e) => Promise.reject(e));
};

interface UpdateAddressArgs extends AddAddressArgs {
  addressId: string;
}

export const updateAddress = ({
  addressId,
  city,
  isDefault,
  mobileNumber,
  state,
  fullName,
  addressLineOne,
  addressLineTwo,
  country,
  pincode,
  countryId,
  regionCode,
  regionId,
  email,
}: UpdateAddressArgs) => {
  const body = {
    address: {
      addressId,
      city,
      isDefault,
      mobileNumber,
      state,
      fullName,
      addressLineOne,
      addressLineTwo,
      country,
      pincode,
      countryId,
      regionCode,
      regionId,
      email,
    },
  };

  const url = getUrl(ROUTES.UPDATE_ADDRESS);

  return ApiHelper(url, {
    method: 'POST',
    body,
    headers: {
      'x-web': '1',
    },
    isBodyJSON: true,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }
    const res = await response.json();

    if (res.status !== 'success') {
      return Promise.reject(res);
    }

    return { success: true, message: res.message };
  });
};

export const isPincodeServicable = (pincode: string) => {
  const body = { pincode };

  const url = getUrl(ROUTES.POSTCODE_SERVICABLE);

  return ApiHelper(url, {
    method: 'POST',
    body,
    isBodyJSON: true,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.status !== 'success') {
      return Promise.reject(res);
    }

    const { state, ...rest } = res.response;

    return {
      ...rest,
      regionId: state,
    } as PincodeServicable;
  });
};

export const setShippingAddress = (addressId: string) => {
  const headers = { ...getEddHeader(), ...getONRHeader() };

  const body = {
    address: {
      addressId,
    },
  };

  const url = getUrl(ROUTES.SET_SHIPPING_ADDRESS);

  return ApiHelper(url, {
    method: 'POST',
    body,
    headers: {
      'x-web': '1',
      'new-cart': '1',
      Platform: 'WEBSITE',
      ...headers,
    },
    isBodyJSON: true,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.status !== 'success') {
      return Promise.reject(res);
    }

    return res.response;
  });
};

export const setGuestShippingAddress = (address: Omit<AddressDataApi, 'addressId'>) => {
  const headers = { ...getEddHeader(), ...getONRHeader() };

  const body = {
    address,
  };

  const url = getUrl(ROUTES.SET_SHIPPING_ADDRESS);

  return ApiHelper(url, {
    method: 'POST',
    body,
    headers: {
      'x-web': '1',
      'new-cart': '1',
      Platform: 'WEBSITE',
      ...headers,
    },
    isBodyJSON: true,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.status !== 'success') {
      return Promise.reject(res);
    }

    return res.response;
  });
};
